import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import { differenceInDays } from "date-fns";

import {
  BaseAddressEntryPoint,
  Cs,
  Delivery,
  STATUS_PICKUP,
  STATUS_PICKUP_NORMAL,
  STATUS_PICKUP_RETURN,
  STATUS_DELIVERY_WAITING,
  STATUS_DELIVERY_POSTPONED,
  STATUS_DELIVERY_STARTED,
  STATUS_DELIVERY_ALLOCATED,
  STATUS_CS,
  STATUS_COMPLETED,
  STATUS_PICKUP_FOR_COUNT,
  STATUS_DELIVERY_ALLOCATED_FOR_COUNT,
  STATUS_DELIVERY_WAITING_FOR_COUNT,
  STATUS_DELIVERY_POSTPONED_FOR_COUNT,
  STATUS_DELIVERY_STARTED_FOR_COUNT,
  STATUS_DELIVERED_FOR_COUNT,
  DELIVERY_TYPE_NORMAL,
  DELIVERY_TYPE_PICKUP,
  DELIVERY_TYPE_RETURN,
  DELIVERY_TYPE_PICKUP_RETURN,
  DELIVERY_STATUS_TYPES,
  ALLOCATION_MATCH_TYPES,
} from "Models";

import api from "services/Api";

import { reOrder } from "utils/common";
import { appVersion, riderTokenNew } from "utils/params";
import { yymmddWithDash, addDays } from "utils/date";

export const DeliveriesContext = createContext({});

const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  FETCH_ALLOCATED: "FETCH_ALLOCATED",
  FETCH_COUNT: "FETCH_COUNT",
  FETCH_COMPLETED: "FETCH_COMPLETED",
  FETCH_DELIVERY_START_BOOKIDS: "FETCH_DELIVERY_START_BOOKIDS",
  FETCH_DELIVERY_START_BOOKIDS_OBJ: "FETCH_DELIVERY_START_BOOKIDS_OBJ",
  FETCH_SCAN_SESSIONS: "FETCH_SCAN_SESSIONS",
  SET_STATUSES: "SET_STATUSES",
  SET_STATUSES_FOR_ONLY_DELIVERY_START: "SET_STATUSES_FOR_ONLY_DELIVERY_START",
  SET_PREVIOUS_STATUSES: "SET_PREVIOUS_STATUSES",
  SET_LIMITED_HOURS_COUNT: "SET_LIMITED_HOURS_COUNT",
};

const DEFAULT_STATUSES = [
  STATUS_PICKUP_NORMAL,
  STATUS_PICKUP_RETURN,
  STATUS_DELIVERY_WAITING,
  STATUS_DELIVERY_POSTPONED,
  STATUS_DELIVERY_STARTED,
  STATUS_CS,
];

const normalizeStatuses = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return [value];
  return null;
};

const readStatusesFromStorage = () => {
  try {
    const stored = localStorage.getItem("statuses");
    if (!stored) return DEFAULT_STATUSES;
    return normalizeStatuses(JSON.parse(stored)) || DEFAULT_STATUSES;
  } catch (e) {
    return DEFAULT_STATUSES;
  }
};

const INITIAL_STATE = {
  statuses: DEFAULT_STATUSES,
  previousStatuses: DEFAULT_STATUSES,
  list: [], // 전체 목록 (배송/수거 포함)
  markerList: [], // 마커목록
  markerListWithSameplace: [], // 마커목록(동일 위치 포함)

  deliveryStartList: [], // 배송 출발 목록
  orderList: [], // 순서지정 목록

  deliveryCompleted: null, // 최근 배송 완료 건

  fetched: false,

  deliveriesAllocated: [], // 배송대기/출발 목록

  scanSessions: [], // 실배송 스캔 세션 목록
  deliveryStartBookIds: [], // 실배송 처리 bookId 목록 (로컬스토리지 / 구버전1)
  deliveryStartBookIdsObj: {}, // 실배송 처리 bookId 목록 (로컬스토리지 / 구버전2)

  count: {
    pickups: 0,
    pickupsNormal: 0,
    pickupsReturn: 0,
    deliveriesAllocated: 0,
    deliveriesBeforeStart: 0,
    deliveriesPostponed: 0,
    deliveriesStarted: 0,
    delivered: 0,
    cses: 0,
  },

  limitedHoursCount: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        list: action.list,
        markerList: action.markerList,
        markerListWithSameplace: action.markerListWithSameplace,
        deliveryStartList: action.deliveryStartList,
        orderList: action.orderList,
        fetched: true,
      };
    case CONSTANTS.FETCH_ALLOCATED:
      return {
        ...state,
        deliveriesAllocated: action.deliveriesAllocated,
      };
    case CONSTANTS.FETCH_COUNT:
      return {
        ...state,
        count: {
          ...state.count,
          ...action.count,
        },
      };
    case CONSTANTS.FETCH_COMPLETED:
      return {
        ...state,
        deliveryCompleted: action.deliveryCompleted,
      };
    case CONSTANTS.FETCH_SCAN_SESSIONS:
      return {
        ...state,
        scanSessions: action.scanSessions,
      };
    case CONSTANTS.SET_STATUSES:
      return {
        ...state,
        statuses: action.statuses,
        previousStatuses: action.statuses,
      };
    case CONSTANTS.SET_STATUSES_FOR_ONLY_DELIVERY_START: {
      const isAlreadyOnlyDeliveryStart =
        state.statuses.length === 1 &&
        state.statuses[0] === STATUS_DELIVERY_STARTED;
      return {
        ...state,
        statuses: [STATUS_DELIVERY_STARTED],
        previousStatuses: isAlreadyOnlyDeliveryStart
          ? state.previousStatuses
          : state.statuses,
      };
    }
    case CONSTANTS.SET_PREVIOUS_STATUSES:
      return {
        ...state,
        statuses: state.previousStatuses,
      };
    case CONSTANTS.SET_LIMITED_HOURS_COUNT:
      return {
        ...state,
        limitedHoursCount: action.limitedHoursCount,
      };
    case CONSTANTS.FETCH_DELIVERY_START_BOOKIDS:
      return {
        ...state,
        deliveryStartBookIds: action.deliveryStartBookIds,
      };
    case CONSTANTS.FETCH_DELIVERY_START_BOOKIDS_OBJ:
      return {
        ...state,
        deliveryStartBookIdsObj: action.deliveryStartBookIdsObj,
      };
    default:
      return state;
  }
};

export const DeliveriesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, null, () => {
    const statuses = readStatusesFromStorage();
    return {
      ...INITIAL_STATE,
      statuses,
      previousStatuses: statuses,
    };
  });

  return (
    <DeliveriesContext.Provider value={{ state, dispatch }}>
      {children}
    </DeliveriesContext.Provider>
  );
};

let fetchAllRequestId = 0;

export const useDeliveriesStore = () => {
  const { dispatch, state } = useContext(DeliveriesContext);

  const isWithinOneMeter = (lat1, lon1, lat2, lon2) => {
    const aLat = Number(lat1);
    const aLon = Number(lon1);
    const bLat = Number(lat2);
    const bLon = Number(lon2);
    if (![aLat, aLon, bLat, bLon].every(Number.isFinite)) return false;
    if (!aLat || !aLon || !bLat || !bLon) return false;
    if (Math.abs(aLat - bLat) > 0.000012 || Math.abs(aLon - bLon) > 0.000015) {
      return false;
    }

    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLon = toRad(bLon - aLon);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(aLat)) *
        Math.cos(toRad(bLat)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)) <= 1;
  };

  const getSamePlaces = (item, deliveries) => {
    if (item.status === STATUS_PICKUP) {
      return deliveries.filter(
        (el) =>
          ((el.senderAddressRoad === item.senderAddressRoad &&
            ((el.compareSenderLocation.longitude -
              item.compareSenderLocation.longitude >=
              0 &&
              el.compareSenderLocation.longitude -
                item.compareSenderLocation.longitude <=
                0.00005) ||
              (item.compareSenderLocation.longitude -
                el.compareSenderLocation.longitude >=
                0 &&
                item.compareSenderLocation.longitude -
                  el.compareSenderLocation.longitude <=
                  0.00005)) &&
            ((el.compareSenderLocation.latitude -
              item.compareSenderLocation.latitude >=
              0 &&
              el.compareSenderLocation.latitude -
                item.compareSenderLocation.latitude <=
                0.00005) ||
              (item.compareSenderLocation.latitude -
                el.compareSenderLocation.latitude >=
                0 &&
                item.compareSenderLocation.latitude -
                  el.compareSenderLocation.latitude <=
                  0.00005))) ||
            (el.compareSenderLocation.longitude ===
              item.compareSenderLocation.longitude &&
              el.compareSenderLocation.latitude ===
                item.compareSenderLocation.latitude) ||
            isWithinOneMeter(
              el.compareSenderLocation.latitude,
              el.compareSenderLocation.longitude,
              item.compareSenderLocation.latitude,
              item.compareSenderLocation.longitude,
            )) &&
          el.bookId !== item.bookId &&
          el.status === item.status,
      );
    } else {
      return deliveries.filter(
        (el) =>
          ((el.receiverAddressRoad === item.receiverAddressRoad &&
            ((el.compareReceiverLocation.longitude -
              item.compareReceiverLocation.longitude >=
              0 &&
              el.compareReceiverLocation.longitude -
                item.compareReceiverLocation.longitude <=
                0.00005) ||
              (item.compareReceiverLocation.longitude -
                el.compareReceiverLocation.longitude >=
                0 &&
                item.compareReceiverLocation.longitude -
                  el.compareReceiverLocation.longitude <=
                  0.00005)) &&
            ((el.compareReceiverLocation.latitude -
              item.compareReceiverLocation.latitude >=
              0 &&
              el.compareReceiverLocation.latitude -
                item.compareReceiverLocation.latitude <=
                0.00005) ||
              (item.compareReceiverLocation.latitude -
                el.compareReceiverLocation.latitude >=
                0 &&
                item.compareReceiverLocation.latitude -
                  el.compareReceiverLocation.latitude <=
                  0.00005))) ||
            (el.compareReceiverLocation.longitude ===
              item.compareReceiverLocation.longitude &&
              el.compareReceiverLocation.latitude ===
                item.compareReceiverLocation.latitude) ||
            isWithinOneMeter(
              el.compareReceiverLocation.latitude,
              el.compareReceiverLocation.longitude,
              item.compareReceiverLocation.latitude,
              item.compareReceiverLocation.longitude,
            )) &&
          el.bookId !== item.bookId &&
          el.status === item.status,
      );
    }
  };

  const getSamePlacesForCs = (item, deliveriesCs) => {
    return deliveriesCs.filter(
      (el) =>
        ((el.deliveryForMarker?.compareReceiverLocation.longitude ===
          item.deliveryForMarker?.compareReceiverLocation.longitude &&
          el.deliveryForMarker?.compareReceiverLocation.latitude ===
            item.deliveryForMarker?.compareReceiverLocation.latitude) ||
          el.deliveryForMarker.receiverAddressRoad ===
            item.deliveryForMarker.receiverAddressRoad ||
          isWithinOneMeter(
            el.deliveryForMarker?.compareReceiverLocation.latitude,
            el.deliveryForMarker?.compareReceiverLocation.longitude,
            item.deliveryForMarker?.compareReceiverLocation.latitude,
            item.deliveryForMarker?.compareReceiverLocation.longitude,
          )) &&
        el.bookId !== item.bookId,
    );
  };

  const getPickupSamePlaces = (item, deliveries) => {
    return deliveries.filter(
      (el) =>
        ((el.senderAddressRoad === item.receiverAddressRoad &&
          ((el.compareSenderLocation.longitude -
            item.compareReceiverLocation.longitude >=
            0 &&
            el.compareSenderLocation.longitude -
              item.compareReceiverLocation.longitude <=
              0.00005) ||
            (item.compareReceiverLocation.longitude -
              el.compareSenderLocation.longitude >=
              0 &&
              item.compareReceiverLocation.longitude -
                el.compareSenderLocation.longitude <=
                0.00005)) &&
          ((el.compareSenderLocation.latitude -
            item.compareReceiverLocation.latitude >=
            0 &&
            el.compareSenderLocation.latitude -
              item.compareReceiverLocation.latitude <=
              0.00005) ||
            (item.compareReceiverLocation.latitude -
              el.compareSenderLocation.latitude >=
              0 &&
              item.compareReceiverLocation.latitude -
                el.compareSenderLocation.latitude <=
                0.00005))) ||
          (el.compareSenderLocation.longitude ===
            item.compareReceiverLocation.longitude &&
            el.compareSenderLocation.latitude ===
              item.compareReceiverLocation.latitude) ||
          isWithinOneMeter(
            el.compareSenderLocation.latitude,
            el.compareSenderLocation.longitude,
            item.compareReceiverLocation.latitude,
            item.compareReceiverLocation.longitude,
          )) &&
        el.bookId !== item.bookId &&
        el.status === STATUS_PICKUP,
    );
  };

  const getPickupSamePlacesForCs = (item, deliveries) => {
    return deliveries.filter(
      (el) =>
        ((el.senderAddressRoad ===
          item.deliveryForMarker?.receiverAddressRoad &&
          ((el.compareSenderLocation.longitude -
            item.deliveryForMarker?.compareReceiverLocation.longitude >=
            0 &&
            el.compareSenderLocation.longitude -
              item.deliveryForMarker?.compareReceiverLocation.longitude <=
              0.00005) ||
            (item.deliveryForMarker?.compareReceiverLocation.longitude -
              el.compareSenderLocation.longitude >=
              0 &&
              item.deliveryForMarker?.compareReceiverLocation.longitude -
                el.compareSenderLocation.longitude <=
                0.00005)) &&
          ((el.compareSenderLocation.latitude -
            item.deliveryForMarker?.compareReceiverLocation.latitude >=
            0 &&
            el.compareSenderLocation.latitude -
              item.deliveryForMarker?.compareReceiverLocation.latitude <=
              0.00005) ||
            (item.deliveryForMarker?.compareReceiverLocation.latitude -
              el.compareSenderLocation.latitude >=
              0 &&
              item.deliveryForMarker?.compareReceiverLocation.latitude -
                el.compareSenderLocation.latitude <=
                0.00005))) ||
          (el.compareSenderLocation.longitude ===
            item.deliveryForMarker?.compareReceiverLocation.longitude &&
            el.compareSenderLocation.latitude ===
              item.deliveryForMarker?.compareReceiverLocation.latitude) ||
          isWithinOneMeter(
            el.compareSenderLocation.latitude,
            el.compareSenderLocation.longitude,
            item.deliveryForMarker?.compareReceiverLocation.latitude,
            item.deliveryForMarker?.compareReceiverLocation.longitude,
          )) &&
        el.bookId !== item.deliveryForMarker?.bookId &&
        el.status === STATUS_PICKUP,
    );
  };

  const getDeliverySamePlacesForCs = (item, deliveries) => {
    return deliveries.filter(
      (el) =>
        ((el.receiverAddressRoad ===
          item.deliveryForMarker?.receiverAddressRoad &&
          ((el.compareReceiverLocation.longitude -
            item.deliveryForMarker?.compareReceiverLocation.longitude >=
            0 &&
            el.compareReceiverLocation.longitude -
              item.deliveryForMarker?.compareReceiverLocation.longitude <=
              0.00005) ||
            (item.deliveryForMarker?.compareReceiverLocation.longitude -
              el.compareReceiverLocation.longitude >=
              0 &&
              item.deliveryForMarker?.compareReceiverLocation.longitude -
                el.compareReceiverLocation.longitude <=
                0.00005)) &&
          ((el.compareReceiverLocation.latitude -
            item.deliveryForMarker?.compareReceiverLocation.latitude >=
            0 &&
            el.compareReceiverLocation.latitude -
              item.deliveryForMarker?.compareReceiverLocation.latitude <=
              0.00005) ||
            (item.deliveryForMarker?.compareReceiverLocation.latitude -
              el.compareReceiverLocation.latitude >=
              0 &&
              item.deliveryForMarker?.compareReceiverLocation.latitude -
                el.compareReceiverLocation.latitude <=
                0.00005))) ||
          (el.compareReceiverLocation.longitude ===
            item.deliveryForMarker?.compareReceiverLocation.longitude &&
            el.compareReceiverLocation.latitude ===
              item.deliveryForMarker?.compareReceiverLocation.latitude) ||
          isWithinOneMeter(
            el.compareReceiverLocation.latitude,
            el.compareReceiverLocation.longitude,
            item.deliveryForMarker?.compareReceiverLocation.latitude,
            item.deliveryForMarker?.compareReceiverLocation.longitude,
          )) &&
        el.bookId !== item.deliveryForMarker?.bookId &&
        (el.status === STATUS_DELIVERY_STARTED ||
          el.status === STATUS_DELIVERY_WAITING ||
          el.status === STATUS_DELIVERY_POSTPONED),
    );
  };

  const getScanNumber = (bookId, sessions) => {
    const today = new Date();

    // ✅ 날짜 내림차순 정렬 (최신 세션 우선)
    const sortedSessions = [...sessions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );

    for (const session of sortedSessions) {
      const entry = session.entries.find((e) => e.bookId === bookId);
      if (entry) {
        const sessionDate = new Date(session.date);
        const daysAgo = differenceInDays(today, sessionDate);
        return daysAgo === 0
          ? `${entry.scanNumber}`
          : `${entry.scanNumber}d${daysAgo}`;
      }
    }
    return null;
  };

  /**
   * 배송목록 정제
   */
  const setDeliveries = useCallback(
    (statuses, deliveries, deliveriesCs, sessions) => {
      let _deliveries = deliveries
        .sort((a, b) => (a.order > b.order ? 1 : -1))
        .sort((a, b) => (a.isPenaltyCorpUser < b.isPenaltyCorpUser ? 1 : -1));

      let _orderList = [];
      let _deliveryStartList = [];
      let _markerList = [];

      deliveriesCs.forEach((item) => {
        let deliveryForMarker = item.deliveryForMarker;
        if (deliveryForMarker) {
          deliveryForMarker.statusText = "cs";
          deliveryForMarker.longitude =
            deliveryForMarker.compareReceiverLocation.longitude;
          deliveryForMarker.latitude =
            deliveryForMarker.compareReceiverLocation.latitude;

          const samePlaces = getSamePlacesForCs(item, deliveriesCs);
          const pickupSamePlaces = getPickupSamePlacesForCs(item, _deliveries);
          const deliverySamePlaces = getDeliverySamePlacesForCs(
            item,
            _deliveries,
          );

          if (pickupSamePlaces.length > 0) {
            deliveryForMarker.samePlacesPickupCount = pickupSamePlaces.length;
            deliveryForMarker.samePlacesPickup = pickupSamePlaces;
          }

          if (deliverySamePlaces.length > 0) {
            deliveryForMarker.samePlacesDeliveryCount =
              deliverySamePlaces.length;
            deliveryForMarker.samePlacesDelivery = deliverySamePlaces;
          }

          if (samePlaces.length > 0) {
            deliveryForMarker.samePlacesCount = samePlaces.length;
            deliveryForMarker.samePlaces = samePlaces.map(
              (d) =>
                new Delivery(
                  d.delivery || {},
                  null,
                  true,
                  Number(appVersion) >= 46
                    ? getScanNumber(d.delivery?.bookId, sessions)
                    : state.deliveryStartBookIdsObj &&
                        Object.keys(state.deliveryStartBookIdsObj).length > 0
                      ? state.deliveryStartBookIdsObj
                      : state.deliveryStartBookIds,
                ),
            );

            if (
              _markerList.find(
                (el) =>
                  ((el.compareReceiverLocation.longitude ===
                    deliveryForMarker.longitude &&
                    el.compareReceiverLocation.latitude ===
                      deliveryForMarker.latitude) ||
                    el.receiverAddress === deliveryForMarker.receiverAddress) &&
                  el.statusText === "cs",
              )
            ) {
            } else {
              _markerList.push(deliveryForMarker);
            }
          } else {
            _markerList.push(deliveryForMarker);
          }
        }
      });

      _deliveries.forEach((item) => {
        if (
          item.status === STATUS_PICKUP ||
          (statuses.includes("PICKUP_NORMAL") &&
            item.statusAddressNotSupported === STATUS_PICKUP)
        ) {
          // 수거
          item.statusText = "pickup";
          item.longitude = item.compareSenderLocation.longitude;
          item.latitude = item.compareSenderLocation.latitude;

          const samePlaces = getSamePlaces(item, _deliveries);

          if (samePlaces.length > 0) {
            item.samePlacesCount = samePlaces.length;
            item.samePlaces = samePlaces;

            if (
              _markerList.find(
                (el) =>
                  el.senderAddress === item.senderAddress &&
                  ((el.compareSenderLocation.longitude - item.longitude >= 0 &&
                    el.compareSenderLocation.longitude - item.longitude <=
                      0.00005) ||
                    (item.longitude - el.compareSenderLocation.longitude >= 0 &&
                      item.longitude - el.compareSenderLocation.longitude <=
                        0.00005)) &&
                  ((el.compareSenderLocation.latitude - item.latitude >= 0 &&
                    el.compareSenderLocation.latitude - item.latitude <=
                      0.00005) ||
                    (item.latitude - el.compareSenderLocation.latitude >= 0 &&
                      item.latitude - el.compareSenderLocation.latitude <=
                        0.00005)) &&
                  (el.status === STATUS_PICKUP ||
                    el.statusAddressNotSupported === STATUS_PICKUP),
              )
            ) {
            } else {
              _markerList.push(item);
            }
          } else {
            _markerList.push(item);
          }
        } else if (
          item.status === STATUS_DELIVERY_STARTED ||
          (statuses.includes("DELIVERY_STARTED") &&
            item.statusAddressNotSupported === STATUS_DELIVERY_STARTED)
        ) {
          // 배송
          item.statusText = "deliver";
          item.longitude = item.compareReceiverLocation.longitude;
          item.latitude = item.compareReceiverLocation.latitude;

          if (item.order) {
            _orderList.push({
              bookId: item.bookId,
              order: item.order,
            });
          }

          const samePlaces = getSamePlaces(item, _deliveries);
          const pickupSamePlaces = getPickupSamePlaces(item, _deliveries);

          if (pickupSamePlaces.length > 0) {
            item.samePlacesPickupCount = pickupSamePlaces.length;
            item.samePlacesPickup = pickupSamePlaces;
          }
          if (samePlaces.length > 0) {
            item.samePlacesCount = samePlaces.length;
            item.samePlaces = samePlaces;

            const samePlacesRed = samePlaces.find(
              (el) => el.keyColor === "highPrice",
            );
            const samePlacesGreen = samePlaces.find(
              (el) => el.keyColor === "limitedDeliveryHours",
            );

            if (samePlacesGreen) {
              item.samePlacekeyColor = "limitedDeliveryHours";
            } else if (
              samePlacesRed &&
              item.keyColor !== "limitedDeliveryHours"
            ) {
              item.samePlacekeyColor = "highPrice";
            }

            if (
              _markerList.find(
                (el) =>
                  el.receiverAddress === item.receiverAddress &&
                  ((el.compareReceiverLocation.longitude - item.longitude >=
                    0 &&
                    el.compareReceiverLocation.longitude - item.longitude <=
                      0.00005) ||
                    (item.longitude - el.compareReceiverLocation.longitude >=
                      0 &&
                      item.longitude - el.compareReceiverLocation.longitude <=
                        0.00005)) &&
                  ((el.compareReceiverLocation.latitude - item.latitude >= 0 &&
                    el.compareReceiverLocation.latitude - item.latitude <=
                      0.00005) ||
                    (item.latitude - el.compareReceiverLocation.latitude >= 0 &&
                      item.latitude - el.compareReceiverLocation.latitude <=
                        0.00005)) &&
                  (el.status === STATUS_DELIVERY_STARTED ||
                    el.statusAddressNotSupported === STATUS_DELIVERY_STARTED),
              )
            ) {
            } else {
              _markerList.push(item);
            }
          } else {
            _markerList.push(item);
          }

          if (item.status === STATUS_DELIVERY_STARTED) {
            _deliveryStartList.push(item);
          }
        } else if (
          item.status === STATUS_DELIVERY_WAITING &&
          statuses.includes(STATUS_DELIVERY_WAITING)
        ) {
          // 배송 대기
          item.statusText = "deliverWaiting";
          item.longitude = item.compareReceiverLocation.longitude;
          item.latitude = item.compareReceiverLocation.latitude;

          const samePlaces = getSamePlaces(item, _deliveries);

          if (samePlaces.length > 0) {
            item.samePlacesCount = samePlaces.length;
            item.samePlaces = samePlaces;

            if (
              _markerList.find(
                (el) =>
                  el.receiverAddress === item.receiverAddress &&
                  ((el.compareReceiverLocation.longitude - item.longitude >=
                    0 &&
                    el.compareReceiverLocation.longitude - item.longitude <=
                      0.00005) ||
                    (item.longitude - el.compareReceiverLocation.longitude >=
                      0 &&
                      item.longitude - el.compareReceiverLocation.longitude <=
                        0.00005)) &&
                  ((el.compareReceiverLocation.latitude - item.latitude >= 0 &&
                    el.compareReceiverLocation.latitude - item.latitude <=
                      0.00005) ||
                    (item.latitude - el.compareReceiverLocation.latitude >= 0 &&
                      item.latitude - el.compareReceiverLocation.latitude <=
                        0.00005)) &&
                  el.status === STATUS_DELIVERY_WAITING,
              )
            ) {
            } else {
              _markerList.push(item);
            }
          } else {
            _markerList.push(item);
          }
        } else if (
          item.status === STATUS_DELIVERY_POSTPONED &&
          statuses.includes(STATUS_DELIVERY_POSTPONED)
        ) {
          // 배송 연기
          item.statusText = "deliverPostponed";
          item.longitude = item.compareReceiverLocation.longitude;
          item.latitude = item.compareReceiverLocation.latitude;

          const samePlaces = getSamePlaces(item, _deliveries);

          if (samePlaces.length > 0) {
            item.samePlacesCount = samePlaces.length;
            item.samePlaces = samePlaces;

            if (
              _markerList.find(
                (el) =>
                  el.receiverAddress === item.receiverAddress &&
                  ((el.compareReceiverLocation.longitude - item.longitude >=
                    0 &&
                    el.compareReceiverLocation.longitude - item.longitude <=
                      0.00005) ||
                    (item.longitude - el.compareReceiverLocation.longitude >=
                      0 &&
                      item.longitude - el.compareReceiverLocation.longitude <=
                        0.00005)) &&
                  ((el.compareReceiverLocation.latitude - item.latitude >= 0 &&
                    el.compareReceiverLocation.latitude - item.latitude <=
                      0.00005) ||
                    (item.latitude - el.compareReceiverLocation.latitude >= 0 &&
                      item.latitude - el.compareReceiverLocation.latitude <=
                        0.00005)) &&
                  el.status === STATUS_DELIVERY_POSTPONED,
              )
            ) {
            } else {
              _markerList.push(item);
            }
          } else {
            _markerList.push(item);
          }
        }
      });

      // 위치추적(배송순서 강제지정) 대상건이 하나라도 있으면
      // deliveryStartList도 해당 대상건만 남기고, 대상건이 아예 없는 회차(위치추적 미사용
      // 기업회원만 있는 경우)라면 배송출발건 전체를 그대로 유지한다.
      // (Map.js의 순서지정 화면에서 마커 표시/선택 범위와 동일한 기준으로 맞추기 위함)
      const hasTrackingEnabledDeliveryStart = _deliveryStartList.some(
        (item) => item.corpUserEnableRiderLocationTracking,
      );

      if (hasTrackingEnabledDeliveryStart) {
        _deliveryStartList = _deliveryStartList.filter(
          (item) => item.corpUserEnableRiderLocationTracking,
        );
      }

      let _resetOrder = reOrder(_orderList);

      let markerList = [];
      let markerListWithSameplace = [];

      _markerList.sort((a, b) =>
        a.compareReceiverLocation.latitude < b.compareReceiverLocation.latitude
          ? 1
          : -1,
      );

      _markerList.sort((a, b) =>
        a.statusMarkerOrder > b.statusMarkerOrder ? 1 : -1,
      );

      _markerList.forEach((marker) => {
        const prevMarkerIndex = markerList.findIndex(
          (d) =>
            d.bookId !== marker.bookId &&
            Number(d.latitude) === Number(marker.latitude) &&
            Number(d.longitude) === Number(marker.longitude) &&
            d.status !== marker.status,
        );

        if (prevMarkerIndex > -1) {
          markerList.splice(prevMarkerIndex, 1);
        }

        markerListWithSameplace.push(marker);
        markerList.push(marker);
      });

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        list: _deliveries,
        orderList: _resetOrder,
        markerList: markerList,
        markerListWithSameplace: markerListWithSameplace,
        deliveryStartList: _deliveryStartList,
      });
    },
    [
      dispatch,
      state.deliveryStartBookIds,
      state.deliveryStartBookIdsObj,
      appVersion,
    ],
  );

  /**
   * 배송 목록
   */
  const fetchAll = useCallback(async () => {
    const requestId = ++fetchAllRequestId;
    const statuses = state.statuses || DEFAULT_STATUSES;

    let typeNormalStatusesPickupRider = []; // type=1, 수거건
    let typeNormalStatusesDeliveryRider = []; // type=1, 배송건

    let typePickupStatusesPickupRider = []; // type=2, 수거건
    let typePickupStatusesDeliveryRider = []; // type=2, 배송건

    let typeReturnStatusesPickupRider = []; // type=3, 수거건
    let typeReturnStatusesDeliveryRider = []; // type=3, 배송건

    let containCses = false;

    statuses.forEach((status) => {
      switch (status) {
        case STATUS_PICKUP_NORMAL:
          typeNormalStatusesPickupRider.push(
            DELIVERY_STATUS_TYPES.PICKUP_SCHEDULED,
          );
          typeNormalStatusesPickupRider.push(
            DELIVERY_STATUS_TYPES.ADDRESS_NOT_SUPPORTED,
          );
          break;
        case STATUS_PICKUP_RETURN:
          typePickupStatusesPickupRider.push(
            DELIVERY_STATUS_TYPES.PICKUP_SCHEDULED,
          );
          typeReturnStatusesPickupRider.push(
            DELIVERY_STATUS_TYPES.PICKUP_SCHEDULED,
          );
          break;
        case STATUS_DELIVERY_POSTPONED:
          typeNormalStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_POSTPONED,
          );
          typePickupStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_POSTPONED,
          );
          typeReturnStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_POSTPONED,
          );
          break;
        case STATUS_DELIVERY_WAITING:
          typeNormalStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_RIDER_ALLOCATED,
          );
          typePickupStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_RIDER_ALLOCATED,
          );
          typeReturnStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_RIDER_ALLOCATED,
          );
          break;
        case STATUS_DELIVERY_STARTED:
          typeNormalStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_STARTED,
          );
          typePickupStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_STARTED,
          );
          typeReturnStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.DELIVERY_STARTED,
          );
          typeNormalStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.ADDRESS_NOT_SUPPORTED,
          );
          typePickupStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.ADDRESS_NOT_SUPPORTED,
          );
          typeReturnStatusesDeliveryRider.push(
            DELIVERY_STATUS_TYPES.ADDRESS_NOT_SUPPORTED,
          );
          break;
        case STATUS_CS:
          containCses = true;
          break;
        default:
          break;
      }
    });

    let promises = [];

    // type=1, 수거건
    if (typeNormalStatusesPickupRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typeNormalStatusesPickupRider,
          type: DELIVERY_TYPE_NORMAL,
          allocatedType: ALLOCATION_MATCH_TYPES.PICKUP,
        }),
      );
    }

    // type=2, 수거건
    if (typePickupStatusesPickupRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typePickupStatusesPickupRider,
          type: DELIVERY_TYPE_PICKUP,
          allocatedType: ALLOCATION_MATCH_TYPES.PICKUP,
        }),
      );
    }

    // type=3, 수거건
    if (typeReturnStatusesPickupRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typeReturnStatusesPickupRider,
          type: DELIVERY_TYPE_RETURN,
          allocatedType: ALLOCATION_MATCH_TYPES.PICKUP,
        }),
      );
    }

    // type=1, 배송건
    if (typeNormalStatusesDeliveryRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typeNormalStatusesDeliveryRider,
          type: DELIVERY_TYPE_NORMAL,
          allocatedType: ALLOCATION_MATCH_TYPES.DELIVERY,
        }),
      );
    }

    // type=2, 배송건
    if (typePickupStatusesDeliveryRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typePickupStatusesDeliveryRider,
          type: DELIVERY_TYPE_PICKUP,
          allocatedType: ALLOCATION_MATCH_TYPES.DELIVERY,
        }),
      );
    }

    // type=3, 배송건
    if (typeReturnStatusesDeliveryRider.length) {
      promises.push(
        api.get("deliveries/v2", riderTokenNew, {
          statuses: typeReturnStatusesDeliveryRider,
          type: DELIVERY_TYPE_RETURN,
          allocatedType: ALLOCATION_MATCH_TYPES.DELIVERY,
        }),
      );
    }

    let sessions = [];
    if (Number(appVersion) >= 46) {
      const response = await api.get("scan-sessions/recent", riderTokenNew, {
        days: 5,
      });
      sessions = response.sessions;
      if (requestId !== fetchAllRequestId) return;
      dispatch({ type: CONSTANTS.FETCH_SCAN_SESSIONS, scanSessions: sessions });
    }

    const results = await Promise.all(promises);

    if (requestId !== fetchAllRequestId) return;

    const _deliveries = results.flat(1);

    const _deliveriesAllocated = await api.get("deliveries", riderTokenNew, {
      status: STATUS_DELIVERY_ALLOCATED,
    });

    if (requestId !== fetchAllRequestId) return;

    const { rows: _deliveriesCs } = await api.get(`cses/all`, riderTokenNew, {
      checked: false,
      from: 30,
    });

    if (requestId !== fetchAllRequestId) return;

    const deliveriesCs = _deliveriesCs.map((d) => new Cs(d));

    const deliveries = _deliveries.map((d) => {
      return new Delivery(
        d,
        _deliveriesAllocated,
        null,
        Number(appVersion) >= 46
          ? getScanNumber(d.bookId, sessions)
          : state.deliveryStartBookIdsObj &&
              Object.keys(state.deliveryStartBookIdsObj).length > 0
            ? state.deliveryStartBookIdsObj
            : state.deliveryStartBookIds,
      );
    });

    const deliveriesAllocated = _deliveriesAllocated.map(
      (d) => new Delivery(d),
    );

    dispatch({
      type: CONSTANTS.FETCH_ALLOCATED,
      deliveriesAllocated,
    });

    if (containCses) {
      setDeliveries(statuses, deliveries, deliveriesCs, sessions);
    } else {
      setDeliveries(statuses, deliveries, [], sessions);
    }
  }, [
    setDeliveries,
    state.deliveryStartBookIds,
    state.deliveryStartBookIdsObj,
    state.statuses,
    appVersion,
  ]);

  /**
   * 실배송 스캔 세션 목록 조회
   */
  const fetchRecentSessions = useCallback(async () => {
    const response = await api.get("scan-sessions/recent", riderTokenNew, {
      days: 5,
    });

    dispatch({
      type: CONSTANTS.FETCH_SCAN_SESSIONS,
      scanSessions: response.sessions,
    });

    return response.sessions;
  }, [dispatch]);

  // const fetchAllocated = useCallback(
  //   async () => {
  //     const response = await api.get('deliveries', riderTokenNew, { status: STATUS_DELIVERY_ALLOCATED });
  //     const deliveriesAllocated = response.map(d => new Delivery(d))

  //     dispatch({
  //       type: CONSTANTS.FETCH_ALLOCATED,
  //       deliveriesAllocated,
  //     });
  //   },
  //   [dispatch],
  // );

  const fetchLimitedHoursCount = useCallback(async () => {
    const response = await api.get("deliveries/v2", riderTokenNew, {
      statuses: [DELIVERY_STATUS_TYPES.DELIVERY_STARTED],
      type: DELIVERY_TYPE_NORMAL,
      allocatedType: ALLOCATION_MATCH_TYPES.DELIVERY,
    });

    const deliveries = response.map((d) => new Delivery(d));
    const isLimitedHours = deliveries.filter((e) => !!e.isLimitedHours);

    dispatch({
      type: CONSTANTS.SET_LIMITED_HOURS_COUNT,
      limitedHoursCount: isLimitedHours.length,
    });
  });

  const fetchCount = useCallback(async () => {
    const pickups = await api.get("deliveries/count", riderTokenNew, {
      state: STATUS_PICKUP_FOR_COUNT,
    });
    const pickupsNormal = await api.get("deliveries/count", riderTokenNew, {
      state: STATUS_PICKUP_FOR_COUNT,
      types: DELIVERY_TYPE_NORMAL,
    });
    const pickupsReturn = await api.get("deliveries/count", riderTokenNew, {
      state: STATUS_PICKUP_FOR_COUNT,
      types: DELIVERY_TYPE_PICKUP_RETURN,
    });
    const deliveriesAllocated = await api.get(
      "deliveries/count",
      riderTokenNew,
      { state: STATUS_DELIVERY_ALLOCATED_FOR_COUNT },
    );
    const deliveriesBeforeStart = await api.get(
      "deliveries/count",
      riderTokenNew,
      { state: STATUS_DELIVERY_WAITING_FOR_COUNT },
    );
    const deliveriesPostponed = await api.get(
      "deliveries/count",
      riderTokenNew,
      { state: STATUS_DELIVERY_POSTPONED_FOR_COUNT },
    );
    const deliveriesStarted = await api.get("deliveries/count", riderTokenNew, {
      state: STATUS_DELIVERY_STARTED_FOR_COUNT,
    });
    const delivered = await api.get("deliveries/count", riderTokenNew, {
      state: STATUS_DELIVERED_FOR_COUNT,
    });
    const { rows: cses } = await api.get(`cses/all`, riderTokenNew, {
      checked: false,
      from: 30,
    });

    dispatch({
      type: CONSTANTS.FETCH_COUNT,
      count: {
        pickups: pickups.count,
        pickupsNormal: pickupsNormal.count,
        pickupsReturn: pickupsReturn.count,
        deliveriesAllocated: deliveriesAllocated.count,
        deliveriesBeforeStart: deliveriesBeforeStart.count,
        deliveriesPostponed: deliveriesPostponed.count,
        deliveriesStarted: deliveriesStarted.count,
        delivered: delivered.count,
        cses: cses?.length,
      },
    });
  }, [dispatch]);

  /**
   * 배송 목록(완료건)
   */
  const fetchCompleted = useCallback(async () => {
    const query = {
      status: STATUS_COMPLETED,
      dateFrom: yymmddWithDash(new Date()),
      dateTo: addDays(new Date(), 1),
    };

    const response = await api.get("deliveries", riderTokenNew, query);

    response.sort((a, b) => {
      return new Date(a.deliveryCompletedDate) >
        new Date(b.deliveryCompletedDate)
        ? -1
        : 1;
    });

    const deliveryCompleted =
      response.length > 0 ? new Delivery(response[0]) : null;

    dispatch({
      type: CONSTANTS.FETCH_COMPLETED,
      deliveryCompleted,
    });

    return deliveryCompleted;
  }, [dispatch]);

  /**
   * CS 목록 조회
   */
  const fetchCsesByAddress = useCallback(
    async (query) => {
      return await api.get("cses/all/by-address", riderTokenNew, query);
    },
    [dispatch],
  );

  /**
   * 리뷰 목록 조회
   */
  const fetchReviewsByAddress = useCallback(
    async (query) => {
      return await api.get("reviews/all/by-address", riderTokenNew, query);
    },
    [dispatch],
  );

  /**
   * 공동현관비밀번호 목록 조회
   */
  const fetchFrontdoorPasswordsByAddress = useCallback(
    async (query) => {
      try {
        const { rows, success } = await api.get(
          "base-address-frontdoor-passwords/all",
          riderTokenNew,
          query,
        );

        return success ? rows : [];
      } catch (e) {
        return [];
      }
    },
    [dispatch],
  );

  /**
   * 건물입구점 목록 조회
   */
  const fetchBaseAddressEntryPoints = useCallback(
    async (query) => {
      const { rows } = await api.get(
        "base-address-entry-points",
        riderTokenNew,
        query,
      );

      return rows.map((row) => new BaseAddressEntryPoint(row));
    },
    [dispatch],
  );

  /**
   * statuses 변경
   */
  const setStatuses = useCallback(
    async (statuses) => {
      const normalized = normalizeStatuses(statuses) || [];
      localStorage.setItem("statuses", JSON.stringify(normalized));

      dispatch({
        type: CONSTANTS.SET_STATUSES,
        statuses: normalized,
      });
    },
    [dispatch],
  );

  /**
   * statuses (배송출발만 허용) — 사용자 설정 키는 덮어쓰지 않음
   */
  const setStatusesOnlyDeliveryStart = useCallback(async () => {
    dispatch({
      type: CONSTANTS.SET_STATUSES_FOR_ONLY_DELIVERY_START,
    });
  }, [dispatch]);

  const setPreviousStatuses = useCallback(async () => {
    dispatch({
      type: CONSTANTS.SET_PREVIOUS_STATUSES,
    });
  }, [dispatch]);

  /**
   * 배송 지연
   */
  const setDelay = useCallback(
    async (
      bookId,
      { reason, reasonType, processType, latitude, longitude },
    ) => {
      await api.put(`deliveries/${bookId}/delay`, riderTokenNew, {
        reason,
        reasonType,
        processType,
        skipStatusChange: true,
        ...(latitude != null && longitude != null
          ? { latitude: Number(latitude), longitude: Number(longitude) }
          : {}),
      });
    },
    [],
  );

  const resetStatuses = useCallback(async () => {
    localStorage.setItem("statuses", JSON.stringify(INITIAL_STATE.statuses));

    dispatch({
      type: CONSTANTS.SET_STATUSES,
      statuses: INITIAL_STATE.statuses,
    });
  }, [dispatch]);

  /**
   * 배송 대기 변경
   */
  const prevDeliveries = useCallback(
    async ({ bookIds, status }) =>
      await api.put("deliveries/prev", riderTokenNew, { bookIds, status }),
    [],
  );

  /**
   * 배송 연기 변경
   */
  const setPostponed = useCallback(
    async (bookId) =>
      await api.put(`deliveries/${bookId}/postponed`, riderTokenNew),
    [],
  );

  /**
   * 실배송 처리 완료 배송 목록(로컬스토리지 / 구버전)
   */
  const setDeliveryStartBookIds = (deliveryStartBookIds) => {
    dispatch({
      type: CONSTANTS.FETCH_DELIVERY_START_BOOKIDS,
      deliveryStartBookIds,
    });
  };

  /**
   * 실배송 처리 완료 배송 목록(로컬스토리지)
   */
  const setDeliveryStartBookIdsObj = (deliveryStartBookIdsObj) => {
    dispatch({
      type: CONSTANTS.FETCH_DELIVERY_START_BOOKIDS_OBJ,
      deliveryStartBookIdsObj,
    });
  };

  return {
    state,
    fetchAll,
    fetchCount,
    fetchLimitedHoursCount,
    fetchCompleted,
    fetchCsesByAddress,
    fetchReviewsByAddress,
    fetchFrontdoorPasswordsByAddress,
    fetchBaseAddressEntryPoints,
    fetchRecentSessions,
    prevDeliveries,
    setStatuses,
    setStatusesOnlyDeliveryStart,
    setPreviousStatuses,
    resetStatuses,
    setPostponed,
    setDelay,
    setDeliveryStartBookIds,
    setDeliveryStartBookIdsObj,
  };
};
